import classnames from 'classnames';
import type { FormContextProps } from '../elements/formContext';
import { Feedback, InputContainer } from '../elements/common';
import { FormContextConsumer } from '../elements/formContext';

interface IImageSelectionOption<T extends string> {
    label: string;
    url: string;
    value: T;
}

interface IImageSelectionProps<T extends string> {
    automation?: string;
    disabled?: boolean;
    error?: string;
    name: string;
    onChange: (value: T | null) => void;
    onDoValidate?: () => void;
    options: IImageSelectionOption<T>[];
    value: T | null;
}

export function ImageSelection<T extends string>(props: FormContextProps<IImageSelectionProps<T>>) {
    return (
        <FormContextConsumer {...props}>
            {(properties) => {
                return (
                    <div
                        className={classnames(
                            'image-selection-group',
                            properties.automation ? 'automation-' + properties.automation : ''
                        )}
                    >
                        {properties.options.map((option, i) => (
                            <ImageSelectInput
                                checked={properties.value === option.value}
                                disabled={properties.disabled}
                                key={i}
                                label={option.label}
                                name={properties.name}
                                onChange={async (value) => {
                                    await properties.onChange(value);
                                    if (properties.onDoValidate) {
                                        await properties.onDoValidate();
                                    }
                                }}
                                url={option.url}
                                value={option.value}
                            />
                        ))}
                        <InputContainer>
                            <Feedback
                                error={
                                    properties.error === 'any_required' || properties.error === 'koi_enum'
                                        ? 'image_required'
                                        : properties.error
                                }
                            />
                        </InputContainer>
                    </div>
                );
            }}
        </FormContextConsumer>
    );
}

interface IImageSelectInputProps<T extends string> {
    checked: boolean;
    disabled?: boolean;
    label: string;
    name: string;
    onChange?: (value: T | null) => void;
    onDoValidate?: () => void;
    url: string;
    value: T;
}

export function ImageSelectInput<T extends string>(props: IImageSelectInputProps<T>) {
    return (
        <div className="img-selection form-check-inline">
            <label
                className="img-selection__label"
                data-dd-privacy="allow"
            >
                <input
                    checked={props.checked}
                    className="img-selection__input"
                    disabled={props.disabled}
                    name={props.name}
                    onChange={async (event) => {
                        if (props.onChange) {
                            await props.onChange(event.target.checked ? props.value : null);
                        }
                        if (props.onDoValidate) {
                            await props.onDoValidate();
                        }
                    }}
                    type="checkbox"
                    value={props.value}
                />
                <img
                    alt={props.label}
                    src={props.url}
                />
                <div className="img-selection__text-label">
                    <span>{props.label}</span>
                </div>
            </label>
        </div>
    );
}
